import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui.js';

export function SelectionActionBar({ count, children, onClear }: {
  count: number;
  children: ReactNode;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const bar = useRef<HTMLDivElement>(null);
  const spacer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (count === 0 || !bar.current || !spacer.current) return;
    const element = bar.current;
    const reserve = spacer.current.parentElement;
    if (!reserve) return;
    const previousPadding = reserve.style.paddingBlockEnd;
    const originalPadding = Number.parseFloat(window.getComputedStyle(reserve).paddingBlockEnd) || 0;
    const update = () => { reserve.style.paddingBlockEnd = `${originalPadding + element.getBoundingClientRect().height + 24}px`; };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(element);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect(); window.removeEventListener('resize', update);
      reserve.style.paddingBlockEnd = previousPadding;
    };
  }, [count]);
  if (count === 0) return null;
  const toolbar = (
    <div className="selection-action-bar" ref={bar} role="region" aria-label={t('common.selected', { count })}>
      <span className="selection-action-bar__count" aria-live="polite">{t('common.selected', { count })}</span>
      <div className="selection-action-bar__actions">
        {children}
        <Button variant="ghost" onClick={onClear}>{t('common.clear')}</Button>
      </div>
    </div>
  );
  return <>
    <div className="selection-action-bar__spacer" ref={spacer} aria-hidden="true" />
    {typeof document !== 'undefined' && document.body ? createPortal(toolbar, document.body) : toolbar}
  </>;
}
